import React from 'react';
import { motion } from 'framer-motion';

const COLLEGE_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/6m859peh_36884FE1-C3B7-4CC8-9F76-C9962EAD94FA.jpg';

const BLUE = '#C9CDD4'; // silver accent (matches site theme)

const milestones = [
  { year: '2017', label: 'Conference Player of the Year' },
  { year: 'NCAA Division I', label: 'South Dakota State' },
  { year: 'Professional Basketball', label: 'Slovakia' },
];

export const AboutDillon = () => {
  return (
    <section
      id="about-dillon"
      data-testid="about-dillon"
      className="relative bg-black py-16 sm:py-14 sm:py-16 lg:py-20 lg:py-24 px-6 border-y border-neutral-900 overflow-hidden"
    >
      {/* Subtle blue glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          background: `radial-gradient(ellipse at 70% 30%, ${BLUE}18, transparent 55%)`,
        }}
      />

      <div className="relative container mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-10 lg:gap-16 items-center">
          {/* Left: College photo full column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full aspect-[3/4] lg:aspect-auto lg:h-[640px] rounded-lg overflow-hidden"
          >
            <img
              src={COLLEGE_IMG}
              alt="Alou Dillon — Purdue Northwest"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'linear-gradient(to bottom, transparent 70%, rgba(0,0,0,0.55) 100%)' }}
            />
            <span
              className="absolute bottom-4 left-4 font-display text-white uppercase text-[11px] tracking-[0.25em] px-3 py-1.5 rounded"
              style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}
            >
              Purdue Northwest
            </span>
          </motion.div>

          {/* Right: Copy */}
          <div className="relative">
            {/* Eyebrow */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-4 mb-5"
            >
              <p
                className="font-display text-xs sm:text-sm uppercase tracking-[0.4em] whitespace-nowrap"
                style={{ color: BLUE }}
              >
                The Story Behind The Blueprint
              </p>
              <div
                className="h-px flex-1"
                style={{ background: `linear-gradient(to right, ${BLUE}, transparent)` }}
              />
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.75, delay: 0.1 }}
              data-testid="about-heading"
              className="font-display text-white text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight leading-[0.9]"
            >
              About <br className="sm:hidden" />
              Alou Dillon
            </motion.h2>

            {/* Blue underline */}
            <motion.div
              initial={{ scaleX: 0, opacity: 0 }}
              whileInView={{ scaleX: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="mt-4 mb-8 h-[3px] w-32 origin-left rounded-full"
              style={{ background: BLUE }}
            />

            {/* Bio copy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="space-y-5 text-gray-300 text-[15px] sm:text-base leading-relaxed"
              data-testid="about-bio"
            >
              <p>
                I graduated from Wauwatosa West High School in 2017 after earning First Team All-Conference,
                First Team All-State, and Conference Player of the Year honors.
              </p>
              <p>
                I continued my career at the Division I level with South Dakota State, where I played for
                three seasons under head coach T.J. Otzelberger, now the head coach at Iowa State.
              </p>
              <p>
                After transferring, injuries and mental-health challenges forced me to step away from
                basketball for two years. During that time, I rebuilt myself physically and mentally,
                regained strength following a meniscus injury, and prepared for another opportunity.
              </p>
              <p>
                I finished my collegiate career at Purdue Northwest, averaging 16.3 points per game,
                shooting 41% from three, and earning Second Team All-GLIAC honors. My journey eventually
                led to signing a professional contract in Slovakia's top basketball league.
              </p>
              <p>
                Today, I'm dedicated to helping athletes reach levels they may not yet believe are
                possible. Through basketball development and mentorship, I help players build the
                confidence, discipline, and daily habits required to pursue their goals — on and off
                the court.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.8, delay: 0.15 }}
          data-testid="about-timeline"
          className="mt-16 lg:mt-20"
        >
          {/* Line + dots + labels row */}
          <div className="relative">
            {/* Horizontal line */}
            <div className="absolute left-0 right-0 top-[10px] h-px hidden sm:block"
              style={{ background: `linear-gradient(to right, transparent, ${BLUE}66, ${BLUE}66, transparent)` }}
            />

            <div className="grid sm:grid-cols-3 gap-8 sm:gap-4">
              {milestones.map((m, i) => (
                <motion.div
                  key={m.year}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 + i * 0.15 }}
                  className="flex flex-col items-center text-center relative"
                >
                  {/* Dot */}
                  <div className="relative">
                    <div
                      className="w-5 h-5 rounded-full ring-4 ring-black relative z-10"
                      style={{ background: BLUE, boxShadow: `0 0 24px ${BLUE}99` }}
                    />
                  </div>
                  <div className="mt-5">
                    <p className="font-display text-white uppercase text-sm sm:text-base tracking-[0.15em]">
                      {m.year}
                    </p>
                    <p className="text-gray-400 uppercase text-xs sm:text-sm tracking-[0.2em] mt-1">
                      {m.label}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
